import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { TutorialCard } from '@/components/TutorialCard';
import { useAppData } from '@/hooks/useAppData';
import { useUserActions } from '@/hooks/useUserActions';
import { Input } from '@/components/ui/input';
import { ChevronRight, Search, Home as HomeIcon } from 'lucide-react';

export default function CategoryDetail() {
  const { id } = useParams<{ id: string }>();
  const { tutorials, categories } = useAppData();
  const { favorites, toggleFavorite, addHistory } = useUserActions();
  const [searchTerm, setSearchTerm] = useState('');

  const category = categories.find((c) => c.id === id);
  const filteredTutorials = tutorials
    .filter((t) => t.categoryId === id)
    .filter((t) => 
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.tag.toLowerCase().includes(searchTerm.toLowerCase())
    );

  if (!category) {
    return (
      <Layout>
        <div className="container mx-auto py-24 text-center">
          <h1 className="text-3xl font-bold mb-4">分类未找到</h1>
          <Link to="/" className="text-primary hover:underline">返回首页</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 md:px-6 py-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-8 overflow-x-auto whitespace-nowrap py-1">
          <Link to="/" className="flex items-center gap-1 hover:text-primary transition-colors">
            <HomeIcon className="h-3.5 w-3.5" /> 首页
          </Link>
          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          <span className="text-foreground font-medium">{category.name}</span>
        </nav>

        {/* Header & Search */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div className="flex flex-col gap-2">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">{category.name}</h1>
            <p className="text-muted-foreground">
              共发现 {filteredTutorials.length} 个相关资源
            </p>
          </div>

          <div className="relative w-full md:w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="在此分类下搜索..."
              className="pl-10 h-11"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredTutorials.map((tutorial) => (
            <TutorialCard
              key={tutorial.id}
              tutorial={tutorial}
              isFavorited={favorites.includes(tutorial.id)}
              onFavoriteToggle={toggleFavorite}
              onClick={addHistory}
            />
          ))}
        </div>

        {filteredTutorials.length === 0 && (
          <div className="py-24 text-center border rounded-2xl bg-muted/20">
            <div className="max-w-md mx-auto">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
              <h3 className="text-lg font-bold mb-2">未找到相关内容</h3>
              <p className="text-muted-foreground">试试更换搜索词或浏览其他分类</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
